import { EMPTY_ARRAY } from 'constants/defaults';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { HourDraft } from 'types/hour';

// Export some self-contained hours draft selectors to avoid dependency cycles

export const readHourDraftId = (state: IState) => state.hours.draft?.id ?? null;
export const readHourDraftDay = (state: IState): string | undefined => state.hours.draft?.day;
const readHourDraftTimestampFrom = (state: IState): string | null | undefined =>
  state.hours.draft?.from;
export const readHourDraftUser = (state: IState): { id: number } | undefined =>
  state.hours.draft?.user;
const readHourDraftEntryIds = (state: IState): Array<number> =>
  state.hours.draft?.entry_ids ?? EMPTY_ARRAY;
const readHourDraftEntryCount = (state: IState): number => {
  return (state.hours.draft?.entry_ids || []).length;
};
const readHourDraftSuggestionId = (state: IState): number | null | undefined =>
  state.hours.draft?.suggestion_id;
const readHourDraft = (state: IState): HourDraft | undefined => state.hours.draft;
const readHourDraftExternalLinks = (state: IState) => state.hours.draft?.external_links;
const readHourDraftShowAllProjectsForDraft = (state: IState) =>
  state.hours.draft?._showAllProjectsForDraft;
const readHourDraftProject = (state: IState): number | null | undefined =>
  state.hours.draft?.project; // id reference
const readLoggedWeeklyTotal = (state: IState) => state.hours.loggedWeeklyTotal;
export const readAutoNoteStrategy = (state: IState) => state.hours.autoNoteStrategy;
export const getLoggedWeeklyTotal = readLoggedWeeklyTotal;
export const getHourDraft = readHourDraft;
export const getHourDraftId = readHourDraftId;
export const getHourDraftDay = (state: IState) => readHourDraftDay(state) || false;
export const getHourDraftExternalLinks = createSelector(
  [readHourDraftExternalLinks],
  links => links ?? EMPTY_ARRAY
);
export const getHourDraftShowAllProjectsForDraft = createSelector(
  [readHourDraftShowAllProjectsForDraft],
  value => value === true
);

export const getHourDraftTagIds = createSelector([readHourDraft], draft => draft?.label_ids || []);

export const getHourDraftTimestamps = createSelector(
  [readHourDraft],
  draft => draft?.timestamps || EMPTY_ARRAY
);

export const getHourDraftHasEntries = createSelector(
  [readHourDraftEntryCount],
  (count): boolean => count > 0
);
// Note: When we support multiple timestamps, use the FIRST from here:
export const getHourDraftScrollToTimestamp = createSelector(
  [readHourDraftTimestampFrom],
  (from): string | null => from || null
);
export const getHourDraftUserId = createSelector([readHourDraftUser], (user): number | null =>
  user ? user.id : null
);
export const getHourDraftEntryIds = readHourDraftEntryIds;

export const getHourDraftSuggestionId = (state: IState) => readHourDraftSuggestionId(state) || null;

// Empty drafts via hourDraftFactory are objects
export const getHourDraftProjectId = createSelector(
  [readHourDraftProject],
  (project): number | null => {
    if (!project) {
      return null;
    }
    // @ts-expect-error
    return !isNaN(project) ? project : project.id;
  }
);

export const getHourDraftForecastId = createSelector([readHourDraft], draft => draft?.forecast_id);
